/**
Copyright (c) 2022–2026 Podstack.ai
All rights reserved.
Proprietary and Confidential.
Unauthorized copying of this file, via any medium, is strictly prohibited.
This file is part of the Podstack Platform.
*/

import axios, { AxiosInstance, AxiosError } from 'axios';
import toast from 'react-hot-toast';
import { useAuthStore } from '../store/authStore';
import type {
  AWSConnection,
  AnalyzeKickedResponse,
  CreateConnectionRequest,
  FinopsMe,
  FinopsOperator,
  FinopsSummary,
  PatchConnectionRequest,
  Recommendation,
  SyncKickedResponse,
  VerifyConnectionResponse,
} from '../types/finops';

const FINOPS_API_URL = process.env.REACT_APP_FINOPS_API_URL || 'https://cloud.podstack.ai/finops/api/v1';

// createFinopsApiClient mirrors services/api.ts's createApiClient (Bearer
// token from localStorage on every request, 401 -> clear session and bounce
// to login). It's a standalone client - not a method added to platformApi -
// because finops-service is its own deployment behind its own gateway prefix
// (see REACT_APP_FINOPS_API_URL), not part of platform-service.
const createFinopsApiClient = (baseURL: string): AxiosInstance => {
  const client = axios.create({
    baseURL,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  client.interceptors.request.use((config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    const projectId = useAuthStore.getState().user?.selected_project_id;
    if (projectId && !config.headers['X-Project-Id']) {
      config.headers['X-Project-Id'] = projectId;
    }
    const orgId = localStorage.getItem('selected_org_id');
    if (orgId && !config.headers['X-Organization-Id']) {
      config.headers['X-Organization-Id'] = orgId;
    }
    return config;
  });

  client.interceptors.response.use(
    (response) => response,
    (error: AxiosError<{ error?: string }>) => {
      if (error.response?.status === 401) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        window.location.href = `${window.location.origin}/portal/login`;
        return Promise.reject(error);
      }

      const message = error.response?.data?.error || 'An error occurred';
      toast.error(message);
      return Promise.reject(error);
    }
  );

  return client;
};

export const finopsApi = createFinopsApiClient(FINOPS_API_URL);

// Every relative path below repeats the "/finops" segment because
// finops-service mounts its own routes under /api/v1/finops (see
// services/finops-service/routes/routes.go) while FINOPS_API_URL already
// carries the gateway's routing prefix + /api/v1. Concatenated they
// reproduce the exact backend path, e.g. POST /api/v1/finops/connections.
const BASE = '/finops/connections';

export const finopsService = {
  createConnection: (data: CreateConnectionRequest) =>
    finopsApi.post<AWSConnection>(BASE, data),

  listConnections: () =>
    finopsApi.get<{ connections: AWSConnection[] }>(BASE),

  getConnection: (id: string) =>
    finopsApi.get<AWSConnection>(`${BASE}/${id}`),

  patchConnection: (id: string, data: PatchConnectionRequest) =>
    finopsApi.patch<AWSConnection>(`${BASE}/${id}`, data),

  deleteConnection: (id: string) =>
    finopsApi.delete<void>(`${BASE}/${id}`),

  verifyConnection: (id: string) =>
    finopsApi.post<VerifyConnectionResponse>(`${BASE}/${id}/verify`),

  syncInventory: (id: string) =>
    finopsApi.post<SyncKickedResponse>(`${BASE}/${id}/sync`),

  syncCosts: (id: string) =>
    finopsApi.post<SyncKickedResponse>(`${BASE}/${id}/sync-costs`),

  analyze: (id: string) =>
    finopsApi.post<AnalyzeKickedResponse>(`${BASE}/${id}/analyze`),

  listRecommendations: (id: string) =>
    finopsApi.get<{ recommendations: Recommendation[] }>(`${BASE}/${id}/recommendations`),

  getSummary: (id: string) =>
    finopsApi.get<FinopsSummary>(`${BASE}/${id}/summary`),

  getMe: () =>
    finopsApi.get<FinopsMe>('/finops/me'),

  listOperators: (orgId: string) =>
    finopsApi.get<{ operators: FinopsOperator[] }>('/finops/operators', {
      headers: { 'X-Organization-Id': orgId },
    }),

  grantOperator: (orgId: string, userId: string) =>
    finopsApi.post('/finops/operators', { user_id: userId }, {
      headers: { 'X-Organization-Id': orgId },
    }),

  revokeOperator: (orgId: string, userId: string) =>
    finopsApi.delete<void>(`/finops/operators/${userId}`, {
      headers: { 'X-Organization-Id': orgId },
    }),
};
